/**
 * webSpaceDraw.web.ts
 * Web Canvas2D draw helper for the World 2 space scene.
 *
 * Reads a draw list from {@link buildSpaceDrawList} and paints it onto a 2D
 * canvas context. Sibling to the Skia path in `GameCanvas.tsx` so the iOS and
 * web renderers stay visually identical.
 *
 * No game-state mutation. No React.
 *
 * Phase 4 — World 2: Starfield Drift (greybox renderer pass).
 *
 * @module webSpaceDraw
 */

/// <reference lib="dom" />

import { buildSpaceDrawList } from './spaceRender';
import { spaceState } from '../engine/spaceState';
import { drawSpaceBossLayer, drawFighterLasersLayer, drawBomberOrdnanceLayer } from './webSpaceBossDraw';
import { drawSpaceBackground } from './webSpaceBgDraw';
import { drawSpaceCollectiblesLayer } from './webSpaceCollectiblesDraw';
import { ensureEntitySprite } from './spaceEntitySpriteCache';
import {
  type ImageSlot,
  createImageSlot,
  ensureImageLoaded,
} from './lazyImageLoader';
import {
  EXPLOSION_FRAME_WIDTH,
  EXPLOSION_FRAME_HEIGHT,
} from '../engine/explosionVfx';
import explosionSheetUrl from '../../assets/sprites/explosion-sheet.png';
import shipSpriteUrl from '../../assets/sprites/courier-ship.png';
import shipLaserUrl from '../../assets/sprites/ship-laser.png';
import shipMissileUrl from '../../assets/sprites/ship-missile.png';

// DECISION: Vite static-asset imports (replaced original `require('...png')`
// calls that ADR-011 left behind — browser has no `require`, so the originals
// crashed the bundle at module-load time).
const EXPLOSION_SHEET_MODULE: string = explosionSheetUrl;
const SHIP_SPRITE_MODULE: string = shipSpriteUrl;
const SHIP_LASER_MODULE: string = shipLaserUrl;
const SHIP_MISSILE_MODULE: string = shipMissileUrl;

// Module-level cache slots — one per asset. `lazyImageLoader` owns
// the decode lifecycle (single-shot latch, silent-fail on missing
// runtime).
const explosionSheetSlot: ImageSlot = createImageSlot();
const shipSpriteSlot: ImageSlot = createImageSlot();
const laserSpriteSlot: ImageSlot = createImageSlot();
const missileSpriteSlot: ImageSlot = createImageSlot();

// Per-id entity-sprite cache (asteroids, drones, mines) lives in
// `spaceEntitySpriteCache` so this entry-point file stays under the
// AGENTS.md 300-line cap. `ensureEntitySprite` is imported above.

/**
 * Test-only hook: install a decoded ship sprite into the cache so a
 * unit test can exercise the `drawImage` path. `null` clears the
 * cache and resets the lazy-load latch.
 *
 * @param img - Decoded sprite, or `null` to clear.
 * @returns void — mutates module-local cache state.
 */
export function _setShipSpriteForTests(
  img: globalThis.HTMLImageElement | null,
): void {
  shipSpriteSlot.current = img;
  shipSpriteSlot.loadStarted = img !== null;
}

/**
 * Test-only hook: install a decoded laser sprite into the cache.
 * `null` clears the cache and resets the lazy-load latch.
 *
 * @param img - Decoded sprite, or `null` to clear.
 * @returns void — mutates module-local cache state.
 */
export function _setLaserSpriteForTests(
  img: globalThis.HTMLImageElement | null,
): void {
  laserSpriteSlot.current = img;
  laserSpriteSlot.loadStarted = img !== null;
}

/**
 * Test-only hook: install a decoded missile sprite into the cache.
 * `null` clears the cache and resets the lazy-load latch.
 *
 * @param img - Decoded sprite, or `null` to clear.
 * @returns void — mutates module-local cache state.
 */
export function _setMissileSpriteForTests(
  img: globalThis.HTMLImageElement | null,
): void {
  missileSpriteSlot.current = img;
  missileSpriteSlot.loadStarted = img !== null;
}

// Boss / boss-bolt / boss-missile test hooks live in
// `webSpaceBossDraw.web.ts` (the per-frame boss-layer helper module).
// Re-export them here so existing tests keep working without changing
// their import path.
export {
  _setBossSpriteForTests,
  _setBossBoltSpriteForTests,
  _setBossMissileSpriteForTests,
} from './webSpaceBossDraw';

/**
 * Paint one frame of the space scene onto `ctx`.
 *
 * Always fills the full viewport — the caller does not need to clear the
 * canvas first. Safe to call every frame even when the space scene is not
 * running (the draw list will be the empty greybox background only).
 *
 * @param ctx - 2D rendering context for the game canvas.
 * @returns void — the function paints directly onto `ctx` as a side effect.
 */
export function drawSpaceScene(ctx: CanvasRenderingContext2D): void {
  ensureImageLoaded(explosionSheetSlot, EXPLOSION_SHEET_MODULE);
  ensureImageLoaded(shipSpriteSlot, SHIP_SPRITE_MODULE);
  ensureImageLoaded(laserSpriteSlot, SHIP_LASER_MODULE);
  ensureImageLoaded(missileSpriteSlot, SHIP_MISSILE_MODULE);
  const drawList = buildSpaceDrawList();

  // Background wash + tiled starfield + mid-distance decorations.
  // Lives in `webSpaceBgDraw` so this file stays under the 300-line
  // cap. Painted BEFORE the shake save/translate so the parallax
  // planets / twinkles don't strobe.
  drawSpaceBackground(ctx, drawList);

  // DECISION: Camera-shake offset applies to the playfield only —
  // background paint (in `webSpaceBgDraw`) runs above this stanza so
  // the wash + starfield + parallax decorations stay fixed. Skipped
  // when shake is zero so the render-test transform-op assertions
  // for the ship-flip stanza (`[0]`, `[1]`, `[2]`) keep matching.
  const shakeX = spaceState.shakeX;
  const shakeY = spaceState.shakeY;
  const shakeActive = shakeX !== 0 || shakeY !== 0;
  if (shakeActive) {
    ctx.save();
    ctx.translate(shakeX, shakeY);
  }

  // Asteroids first so projectiles and the ship paint on top of them.
  for (const a of drawList.asteroids) {
    const sprite =
      a.spriteId !== undefined ? ensureEntitySprite(a.spriteId) : null;
    if (sprite !== null) {
      ctx.drawImage(sprite, a.x, a.y, a.width, a.height);
    } else {
      ctx.fillStyle = a.color;
      ctx.fillRect(a.x, a.y, a.width, a.height);
    }
  }

  // Enemies paint after asteroids so they're visible when overlapping.
  for (const e of drawList.enemies) {
    const sprite =
      e.spriteId !== undefined ? ensureEntitySprite(e.spriteId) : null;
    if (sprite !== null) {
      ctx.drawImage(sprite, e.x, e.y, e.width, e.height);
    } else {
      ctx.fillStyle = e.color;
      ctx.fillRect(e.x, e.y, e.width, e.height);
    }
  }
  // Fighter-drone lasers paint between drones and the heavier boss /
  // mine layers — in front of the drone, behind a passing missile.
  drawFighterLasersLayer(ctx, drawList);
  // Bomber cluster bombs + their shrapnel paint in the same enemy-
  // weapon band as fighter lasers — same z-order rationale.
  drawBomberOrdnanceLayer(ctx, drawList);

  // Homing mines paint after enemies but before the boss — they're a
  // Level 2-3 environmental hazard layer, distinct from the drone /
  // boss threat streams. State-keyed colour (IDLE slate / ACTIVE red)
  // is already baked into each rect by `buildSpaceDrawList`; the
  // sprite path uses one shared PNG (id = `'mine'`) for both states.
  for (const m of drawList.mines) {
    const sprite =
      m.spriteId !== undefined ? ensureEntitySprite(m.spriteId) : null;
    if (sprite !== null) {
      ctx.drawImage(sprite, m.x, m.y, m.width, m.height);
    } else {
      ctx.fillStyle = m.color;
      ctx.fillRect(m.x, m.y, m.width, m.height);
    }
  }

  // Constellation shards + Star Seed loot. `drawSpaceCollectiblesLayer`
  // blits real sprites when their decode has landed and falls back to a
  // procedural 5-point star until then. Ordered in the same band as
  // mines (post-enemy, pre-boss): they're passive collectibles, not
  // hazards, but z-ordering them alongside mines keeps the playfield
  // band cohesive. Star shape (not flat rect) is required for the
  // fallback so the player still reads them as pickups during the
  // decode gap — see AGENTS.md "Star Seeds (angular star shape)".
  drawSpaceCollectiblesLayer(ctx, drawList);

  // Boss + boss-shield + boss bolts + boss missiles paint in one
  // module-local pass so the asset slots and z-order stay co-located.
  drawSpaceBossLayer(ctx, drawList);

  for (const p of drawList.projectiles) {
    // Prefer the decoded sprite when available; fall back to the
    // greybox colour rect until the laser sprite finishes loading.
    if (laserSpriteSlot.current !== null) {
      ctx.drawImage(laserSpriteSlot.current, p.x, p.y, p.width, p.height);
    } else {
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x, p.y, p.width, p.height);
    }
  }

  // Missiles paint after projectiles so a missile fired this tick reads
  // in front of the laser stream but behind the courier ship below.
  for (const m of drawList.missiles) {
    if (missileSpriteSlot.current !== null) {
      ctx.drawImage(missileSpriteSlot.current, m.x, m.y, m.width, m.height);
    } else {
      ctx.fillStyle = m.color;
      ctx.fillRect(m.x, m.y, m.width, m.height);
    }
  }

  // Nebula fog overlay paints between projectiles and the player ship so
  // the ship + UI telegraphs stay crisp while the rest of the playfield
  // takes a purple cast.
  if (drawList.fog !== null) {
    const f = drawList.fog;
    ctx.fillStyle = f.color;
    ctx.fillRect(f.x, f.y, f.width, f.height);
  }

  if (drawList.ship !== null) {
    const s = drawList.ship;
    // Prefer the decoded sprite when available; fall back to the
    // greybox colour rect until the ship sprite finishes loading.
    if (shipSpriteSlot.current !== null) {
      // DECISION: The Courier-Ship PNG is authored facing LEFT (cockpit
      // on the left). World 2 auto-scrolls the camera rightward, so the
      // ship needs to face RIGHT to read as "flying forward". Mirror via
      // a save/translate/scale/restore stanza so the rest of the scene
      // graph isn't affected.
      ctx.save();
      ctx.translate(s.x + s.width, s.y);
      ctx.scale(-1, 1);
      ctx.drawImage(shipSpriteSlot.current, 0, 0, s.width, s.height);
      ctx.restore();
    } else {
      ctx.fillStyle = s.color;
      ctx.fillRect(s.x, s.y, s.width, s.height);
    }
  }

  // Explosion bursts paint between the ship and the wave telegraphs:
  // the kill flash should sit on top of the playfield (so a burst
  // overlapping the ship reads as "near miss / enemy popped right next
  // to me") while still staying behind the always-on-top wave UI.
  // Prefer the decoded sprite sheet when available; fall back to the
  // greybox colour rect until the sheet finishes loading (or always,
  // for builds without the asset).
  const sheet = explosionSheetSlot.current;
  for (const x of drawList.explosions) {
    if (sheet !== null && x.spriteFrame !== undefined) {
      ctx.drawImage(
        sheet,
        x.spriteFrame * EXPLOSION_FRAME_WIDTH,
        0,
        EXPLOSION_FRAME_WIDTH,
        EXPLOSION_FRAME_HEIGHT,
        x.x,
        x.y,
        x.width,
        x.height,
      );
    } else {
      ctx.fillStyle = x.color;
      ctx.fillRect(x.x, x.y, x.width, x.height);
    }
  }

  if (shakeActive) {
    ctx.restore();
  }
}
